import { useEffect, useRef, useState } from 'react'
import { Row, Col } from 'react-bootstrap'
import { AlertMessage, IPageProps, IRootState, DcsPermissions } from 'types'
import { FormSwitch, PageHeader, SaveButton, Spinner, MessageAlert } from 'components'
import { UpdateF4SalesEngineSettingsDto } from 'generated/v1/v1IntegrationsApi'
import { FormElement, Field, Form, FormClassComponent } from '@progress/kendo-react-form'
import { useDispatch, useSelector } from 'react-redux'
import { endLoadingAction, startLoadingAction } from 'state/actions/appActions'
import { V1ApiIntegrationsClient } from 'core/http-common'
import { AppDispatch } from 'state/store'
import toast from 'core/toast'
import ConfirmModal from 'components/Modals/ConfirmModal'
import { hasPermission } from 'state/reduxHelpers'
import { DEFAULT_PERMISSION_MESSAGE } from 'core/consts'
import { getErrors } from 'core/helpers'
import './F4SettingsPage.css'

interface IF4Settings {
    dontUseCarleton: boolean
    f4RequestBodyLoggingEnabled: boolean
}

const DEFAULT_SETTINGS: IF4Settings = {
    dontUseCarleton: false,
    f4RequestBodyLoggingEnabled: false,
}

const F4SettingsPage = ({ title }: IPageProps) => {
    const dispatch = useDispatch<AppDispatch>()
    const loading = useSelector((state: IRootState) => state.app.loading)
    const formRef = useRef<FormClassComponent>(null)

    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [initialValues, setInitialValues] = useState<IF4Settings>(DEFAULT_SETTINGS)
    const [isFormModified, setIsFormModified] = useState(false)
    const [loaded, setLoaded] = useState(false)
    const [alert, setAlert] = useState<AlertMessage>()

    useEffect(() => {
        if (!hasPermission(DcsPermissions.CacheAdmin)) {
            toast.error(DEFAULT_PERMISSION_MESSAGE)
            setLoaded(true)
            return
        }
        fetchSettings()
    }, [])

    const fetchSettings = async () => {
        try {
            dispatch(startLoadingAction())
            const response = await V1ApiIntegrationsClient.getF4SalesEngineSettings()
            setInitialValues({
                dontUseCarleton: response.dontUseCarleton ?? false,
                f4RequestBodyLoggingEnabled: response.f4RequestBodyLoggingEnabled ?? false,
            })
        } catch (e) {
            setAlert({ variant: 'danger', messages: getErrors(e) })
        } finally {
            setLoaded(true)
            dispatch(endLoadingAction())
        }
    }

    /* eslint-disable @typescript-eslint/no-explicit-any */
    const handleFieldChange = (field: keyof IF4Settings) => (e: any) => {
        const form = formRef.current
        if (!form) return
        const current: IF4Settings = {
            dontUseCarleton: form.valueGetter('dontUseCarleton') ?? false,
            f4RequestBodyLoggingEnabled: form.valueGetter('f4RequestBodyLoggingEnabled') ?? false,
            [field]: e.value ?? false,
        }
        const modified =
            current.dontUseCarleton !== initialValues.dontUseCarleton ||
            current.f4RequestBodyLoggingEnabled !== initialValues.f4RequestBodyLoggingEnabled
        setIsFormModified(modified)
    }

    const handleSubmit = () => {
        setShowConfirmModal(true)
    }

    const handleConfirmSave = async () => {
        const form = formRef.current
        if (!form) return

        try {
            dispatch(startLoadingAction())
            setShowConfirmModal(false)
            const updateDto = new UpdateF4SalesEngineSettingsDto({
                dontUseCarleton: form.valueGetter('dontUseCarleton') ?? false,
                f4RequestBodyLoggingEnabled: form.valueGetter('f4RequestBodyLoggingEnabled') ?? false,
            })
            const response = await V1ApiIntegrationsClient.updateF4SalesEngineSettings(updateDto)

            if (response.success) {
                setInitialValues({
                    dontUseCarleton: response.payload?.dontUseCarleton ?? false,
                    f4RequestBodyLoggingEnabled: response.payload?.f4RequestBodyLoggingEnabled ?? false,
                })
                setIsFormModified(false)
                toast.success('FrazerOne settings updated successfully.')
                setAlert(undefined)
            } else {
                setAlert({ variant: 'danger', messages: getErrors(response.errors) })
            }
        } catch (e) {
            setAlert({ variant: 'danger', messages: getErrors(e) })
        } finally {
            dispatch(endLoadingAction())
        }
    }

    const handleCancelSave = () => {
        setShowConfirmModal(false)
    }

    if (!loaded) {
        return <Spinner center />
    }

    return (
        <div id='F4SettingsPage'>
            <PageHeader title={title} />

            <MessageAlert alert={alert} />

            <Form
                ref={formRef}
                ignoreModified={true}
                onSubmit={handleSubmit}
                initialValues={{
                    dontUseCarleton: initialValues.dontUseCarleton,
                    f4RequestBodyLoggingEnabled: initialValues.f4RequestBodyLoggingEnabled,
                }}
                render={() => (
                    <FormElement>
                        <div className='bg-light border p-3'>
                            <Row className='mb-3'>
                                <Col md={12} className='mb-3 mb-md-0'>
                                    <Field
                                        name='dontUseCarleton'
                                        label='Carleton Integration for FrazerOne Sales Engine'
                                        component={FormSwitch}
                                        onChange={handleFieldChange('dontUseCarleton')}
                                        onLabel='Disabled'
                                        offLabel='Enabled'
                                    />
                                </Col>
                            </Row>
                            <Row className='mb-3'>
                                <Col md={12} className='mb-3 mb-md-0'>
                                    <Field
                                        name='f4RequestBodyLoggingEnabled'
                                        label='FrazerOne Request Body Logging'
                                        component={FormSwitch}
                                        onChange={handleFieldChange('f4RequestBodyLoggingEnabled')}
                                        onLabel='Enabled'
                                        offLabel='Disabled'
                                    />
                                </Col>
                            </Row>
                        </div>
                        <Row className='mt-3'>
                            <Col xs={12} sm={4}>
                                <SaveButton
                                    disabled={!isFormModified || loading}
                                    type='submit'
                                />
                            </Col>
                        </Row>
                    </FormElement>
                )}
            />

            <ConfirmModal
                title='Confirm Action'
                content='Are you sure you want to save these FrazerOne settings?'
                show={showConfirmModal}
                loading={loading}
                errors={[]}
                onCancel={handleCancelSave}
                onSave={handleConfirmSave}
                confirmButtonTitle='Confirm'
                cancelButtonTitle='Cancel'
            />
        </div>
    )
}

export default F4SettingsPage
